import axios from 'axios';
import endpoints from 'constants/endpoints';
import { stateAbbreviations } from 'constants/enum';
import { safeAWSCallPost } from 'helpers/firebaseSetup';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

const INITIAL_VALUES = {
  searchTerm: '',
  agents: [],
  carriers: [],
  states: [],
  initial_date: getMonthsUntilPresent(),
  agentStatus: ['Active']
};

function getMonthsUntilPresent(
  startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1))
) {
  if (!(startDate instanceof Date) || isNaN(startDate)) {
    console.error('Invalid Date object provided.');
    return;
  }

  const currentDate = new Date();
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  let months = [];

  while (start < end) {
    const month = String(start.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = start.getFullYear();
    // Increment by one month
    start.setMonth(start.getMonth() + 1);
    months.push(`${month}-${year}`);
  }

  return months;
}

const useComissionsReco = ({
  setLocalLoader,
  setFilterCount,
  setShowFilters
}) => {
  const cancelTokenSource = axios.CancelToken.source();

  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(50);
  const [data_for_table, setDataForTable] = useState([]);
  const [policies_records_count, setBobRecordsCount] = useState(0);
  const [triggerFetch, setTriggerFetch] = useState(1);
  const [formData, setformData] = useState(INITIAL_VALUES);

  const { satementsCarriersArray } = useSelector(state => state.appointments);

  const carriersOptions = React.useMemo(() => {
    return satementsCarriersArray
      .map(option => {
        return {
          type: option.type,
          name: option.name,
          id: option.AgencyBloc_CarrierID,
          Callidus_Carrier_Names: option.Callidus_Carrier_Names
        };
      })
      .concat([
        {
          type: 'Marketplace',
          name: 'Other',
          id: '',
          Callidus_Carrier_Names: 'Other'
        }
      ])
      .filter(carrier => carrier.type);
  }, [satementsCarriersArray]);

  React.useEffect(() => {
    if (formData.agents.length === 0) return;
    // HS data
    getRecoData(currentPage, formData, true);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, [triggerFetch]);

  const getRecoData = useCallback(
    (page, filters, skipCountPages) => {
      setLocalLoader(true);
      safeAWSCallPost(
        endpoints.aws.BobCommissionReconciliation,
        {
          page: page - 1,
          records_per_page: currentPageSize,
          filters: JSON.stringify({
            ...filters,
            states: filters.states.map(state => stateAbbreviations[state]),
            agents: filters.agents.map(agent => agent.npn),
            skipCountPages
          }),
          agents: filters.agents.map(agent => agent.agentId)
        },
        false,
        cancelTokenSource.token
      )
        .then(response => {
          setLocalLoader(false);
          setDataForTable(response.data.records);
          if (!skipCountPages) {
            setBobRecordsCount(response.data.totalRows);
          }
        })
        .catch(() => {
          setLocalLoader(false);
          setDataForTable([]);
          setBobRecordsCount(0);
        });

      const tbodyElement = document.getElementById(
        'customers-bob-reco-table-body'
      );
      tbodyElement.scrollTop = 0;
    },
    [currentPageSize]
  );

  const clearFilters = useCallback(() => {
    const clearedFormData = {
      ...INITIAL_VALUES,
      agents: [...formData.agents]
    };
    // Calculate filter count: agentStatus always has 'Active' by default, so count is 1
    setFilterCount(1);
    setformData(() => clearedFormData);
  }, [setFilterCount, setformData, formData.agents]);

  const applyFilters = useCallback(() => {
    if (formData.agents.length === 0) return;
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    getRecoData(currentPage, formData, false);
    setShowFilters(false);
  }, [setCurrentPage, currentPage, getRecoData, formData]);

  const handleChangeSelectValue = useCallback(
    (_values, property) => {
      let values = [];
      if (typeof _values === 'string') {
        values = formData[property][0] === _values ? [] : [_values];
      } else {
        values = _values;
      }
      if (property === 'initial_date') {
        const [month, year] = values[0].split('-');
        setformData(prevState => ({
          ...prevState,
          [property]: getMonthsUntilPresent(
            new Date(parseInt(year), parseInt(month) - 1, 1)
          )
        }));
        return;
      }
      if (
        formData[property].length === 0 &&
        values.length > 0 &&
        property !== 'agents'
      ) {
        setFilterCount(count => (count += 1));
      } else if (
        formData[property].length > 0 &&
        values.length === 0 &&
        property !== 'agents'
      ) {
        setFilterCount(count => (count -= 1));
      }
      setformData(prevState => ({
        ...prevState,
        [property]: values
      }));
    },
    [setformData, formData, setFilterCount]
  );

  return {
    data_for_table,
    currentPage,
    setCurrentPage,
    setCurrentPageSize,
    policies_records_count,
    formData,
    setformData,
    clearFilters,
    applyFilters,
    handleChangeSelectValue,
    setTriggerFetch,
    carriersOptions
  };
};

useComissionsReco.propTypes = {
  setLocalLoader: PropTypes.func,
  setFilterCount: PropTypes.func,
  setShowFilters: PropTypes.func
};

export default useComissionsReco;
